import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import ContractorAssistedRegistrationApi from "../../../../infrastructure/api/contractorAssistedRegistrationApi";
import RequestWorkflowTracker from "../../../components/request/tracking/RequestWorkflowTracker";
import { buildCustomerTrackingModel, formatRequestCode } from "../../../components/request/tracking/requestTrackingModel";
import { TRACKING_PAGE_BG } from "../../../components/request/tracking/trackingStyles";
import PartnerCommissionPageShell from "../../partner-commission/components/PartnerCommissionPageShell";

const pick = (data, ...keys) => {
  for (const key of keys) {
    const value = data?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const ContractorAssistedRegistrationTrackingPage = ({ registrationId, listBackPath }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tracking, setTracking] = useState(null);

  const load = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ContractorAssistedRegistrationApi.getTracking(registrationId);
      if (!res.isSuccess) {
        setError(res.message || "بارگذاری وضعیت ناموفق بود.");
        setTracking(null);
        return;
      }
      setTracking(res.data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری وضعیت");
      setTracking(null);
    } finally {
      setLoading(false);
    }
  }, [registrationId]);

  useEffect(() => {
    load();
  }, [load]);

  const model = useMemo(() => {
    if (!tracking) return null;
    return buildCustomerTrackingModel({
      request: {
        referenceID: pick(tracking, "referenceId", "ReferenceId"),
        requestConfirmTitle: pick(tracking, "requestConfirmTitle", "RequestConfirmTitle"),
        requestConfirm: pick(tracking, "requestConfirmTitle", "RequestConfirmTitle")
          ? "InProcess"
          : "NotChecked",
      },
      trackingMilestones: tracking.trackingMilestones || tracking.TrackingMilestones || [],
      parallelTrackingMilestones:
        tracking.parallelTrackingMilestones || tracking.ParallelTrackingMilestones || [],
      activeTrackingMilestoneIndex:
        tracking.activeTrackingMilestoneIndex ?? tracking.ActiveTrackingMilestoneIndex ?? 0,
      parallelInsertAfterMainIndex:
        tracking.parallelInsertAfterMainIndex ?? tracking.ParallelInsertAfterMainIndex ?? 1,
      activeStepIndex: 0,
      shownStepIndex: 0,
    });
  }, [tracking]);

  const referenceId = pick(tracking, "referenceId", "ReferenceId");
  const fullName = pick(tracking, "fullName", "FullName") || "—";
  const maskedMobile = pick(tracking, "maskedMobile", "MaskedMobile") || "—";
  const statusTitle =
    pick(tracking, "requestConfirmTitle", "RequestConfirmTitle") ||
    pick(tracking, "registrationStatusTitle", "RegistrationStatusTitle") ||
    "—";
  const publicNote = pick(tracking, "publicStatusNote", "PublicStatusNote");
  const currentTitle =
    pick(tracking, "currentStageTitle", "CurrentStageTitle") ||
    model?.currentStageTitle ||
    statusTitle;
  const currentDescription =
    pick(tracking, "currentStageDescription", "CurrentStageDescription") ||
    model?.currentStageDescription ||
    publicNote;

  return (
    <PartnerCommissionPageShell
      title="پیگیری درخواست متقاضی"
      description="مراحل پیشرفت پرونده — بدون نمایش جزئیات فرم و مدارک."
      loading={loading}
      error={error}
      onRetry={load}
      empty={!loading && !error && !tracking}
    >
      {listBackPath ? (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(listBackPath)}
          variant="text"
          sx={{ mb: 2 }}
        >
          بازگشت به لیست
        </Button>
      ) : null}

      {tracking && model ? (
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="overline" color="text.secondary">
                      شماره پیگیری
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: 1, mb: 1 }}>
                      {formatRequestCode(referenceId)}
                    </Typography>
                    <StackMeta label="متقاضی" value={fullName} />
                    <StackMeta label="موبایل" value={maskedMobile} />
                    <StackMeta
                      label="کد پرونده"
                      value={pick(tracking, "leadCode", "LeadCode") || "—"}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Chip label={statusTitle} color="primary" sx={{ mb: 1.5 }} />
                    <LinearProgress
                      variant="determinate"
                      value={model.progressPercent ?? 0}
                      sx={{ height: 8, borderRadius: 4, mb: 1 }}
                    />
                    <Typography variant="body2" fontWeight={700}>
                      {currentTitle}
                    </Typography>
                    {currentDescription ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {currentDescription}
                      </Typography>
                    ) : null}
                  </Grid>
                </Grid>
                {publicNote ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {publicNote}
                  </Alert>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ background: TRACKING_PAGE_BG, borderRadius: 2, p: { xs: 1, md: 2 } }}>
              <RequestWorkflowTracker
                timelineSteps={model.timelineSteps}
                parallelTimelineSteps={model.parallelTimelineSteps}
                parallelInsertAfterMainIndex={model.parallelInsertAfterMainIndex}
                activeIndex={model.activeTimelineIndex}
                currentStageTitle={currentTitle}
                currentStageDescription={currentDescription}
              />
            </Box>
          </Grid>
        </Grid>
      ) : null}
    </PartnerCommissionPageShell>
  );
};

const StackMeta = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.5 }}>
    <Box component="span" color="text.secondary">
      {label}:{" "}
    </Box>
    {value}
  </Typography>
);

export default ContractorAssistedRegistrationTrackingPage;
